<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { FilterModal, SelectInput, ThemeProvider } from '@/shared/ui';
  import {
    GENERATED_RECORDS_RANGE_OPTIONS,
    RECORD_STATUS_FILTER_OPTIONS,
  } from '../lib/record-list-query';
  import type { RecordListFilters, RecordStatusFilter } from '../lib/record-list-query';

  const props = defineProps<{
    currentFilters?: RecordListFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: RecordListFilters];
    'clear-filters': [];
  }>();

  const draftStatus = ref<RecordStatusFilter | undefined>(undefined);
  const draftGeneratedRecords = ref('');

  const loadFromFilters = (filters?: RecordListFilters) => {
    draftStatus.value = filters?.status;
    draftGeneratedRecords.value = filters?.generatedRecords ?? '';
  };

  loadFromFilters(props.currentFilters);

  watch(() => props.currentFilters, loadFromFilters, { deep: true });

  const toggleStatus = (status: RecordStatusFilter) => {
    draftStatus.value = draftStatus.value === status ? undefined : status;
  };

  const handleApply = () => {
    emit('update:filter', {
      status: draftStatus.value,
      generatedRecords: draftGeneratedRecords.value || undefined,
    });
  };

  const handleClear = () => {
    draftStatus.value = undefined;
    draftGeneratedRecords.value = '';
    emit('clear-filters');
  };
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-sm"
    @close="emit('close')"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider>
        <div class="p-3">
          <div class="flex flex-col gap-4 px-6 py-3">
            <div class="border-b border-gray-02 pb-3">
              <h4 class="pb-4 text-sm font-medium text-gray-07">
                Status
              </h4>
              <div
                class="flex flex-col items-start gap-1"
                data-testid="record-list-status-options"
              >
                <button
                  v-for="option in RECORD_STATUS_FILTER_OPTIONS"
                  :key="option.id"
                  type="button"
                  :data-testid="`record-list-status-${option.id}`"
                  class="flex w-full items-center justify-start rounded-lg px-3 py-2 text-sm/5"
                  :class="
                    draftStatus === option.id ? 'bg-(--theme-secondary) text-white' : 'text-gray-07'
                  "
                  @click="toggleStatus(option.id)"
                >
                  {{ option.name }}
                </button>
              </div>
            </div>

            <div class="pb-2">
              <h4 class="pb-1.5 text-sm font-medium text-gray-07">
                Registros gerados:
              </h4>
              <SelectInput
                :model-value="draftGeneratedRecords"
                :options="GENERATED_RECORDS_RANGE_OPTIONS"
                label="Selecione a quantidade"
                container-class="w-full min-w-64"
                data-testid="record-list-generated-records-select"
                @update:model-value="draftGeneratedRecords = String($event)"
              />
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
